import { FunctionComponent, MouseEvent as ReactMouseEvent, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material';
import { APP_HEADER_HEIGHT } from '@cbridge-utils/app';
import { DRAWER_RESIZE_BAR_WIDTH } from '../../SidebarNavigationDrawer.constant';
import { clampDrawerWidth } from '../../_helpers/clampDrawerWidth';
import { IResizeHandleBarProps } from './ResizeHandleBar.interface';

export const ResizeHandleBar: FunctionComponent<IResizeHandleBarProps> = (props) => {
  const { width, updateWidth, isResizing, setIsResizing, onResizeEnd } = props;

  const theme = useTheme();

  // The end callback and the last emitted width live in refs so the document listeners are not
  // re-subscribed on every mousemove (the parent re-renders with a new width each move).
  const lastWidthRef = useRef<number | null>(null);
  const onResizeEndRef = useRef(onResizeEnd);

  useEffect(() => {
    onResizeEndRef.current = onResizeEnd;
  }, [onResizeEnd]);

  const handleResizeStart = (e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing) {
      const newWidth = clampDrawerWidth(e.clientX);
      lastWidthRef.current = newWidth;
      updateWidth(newWidth);
    }
  }, [isResizing, updateWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    if (lastWidthRef.current !== null) {
      onResizeEndRef.current?.(lastWidthRef.current);
      lastWidthRef.current = null;
    }
  }, [setIsResizing]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Box
      onMouseDown={handleResizeStart}
      sx={{
        position: 'fixed',
        top: 0,
        height: '100%',
        zIndex: theme.zIndex.drawer + 1,
        left: `${width}px`,
        width: `${DRAWER_RESIZE_BAR_WIDTH}px`,
        cursor: 'col-resize',

        '&:hover': {
          '&::after': {
            opacity: 0.5,
          },
        },

        '&::after': {
          content: '""',
          position: 'absolute',
          top: APP_HEADER_HEIGHT,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: theme.palette.divider,
          opacity: isResizing
            ? 0.5
            : 0,
          transition: 'opacity 0.2s',
        },

        // Make the handle easier to grab by extending the hit area
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-4px',
          width: '12px',
          height: '100%',
          opacity: 0,
        },
      }} />
  );
};
